/**
 * GUEST CHECKOUT ELIGIBILITY
 *
 * ============================================================================
 * RULE: YOU MUST NEVER BE FORCED TO LOG IN TO PAY. DO NOT REGRESS THIS.
 * ============================================================================
 *
 * Every checkout route used to sit behind `ProtectedRoute`, so an unauthenticated
 * buyer was bounced to `/checkout/account` and had to create an account before
 * they could hand over money. That is a severe and entirely self-inflicted
 * conversion leak, and there was never a technical reason for it: every checkout
 * surface already supports guests end to end.
 *
 *   - `CheckoutPage` tags purchases `user_type: 'guest'` and assigns a temp id.
 *   - `StarterCheckoutPage` does the same for plan purchases.
 *   - Entitlement is granted by the Stripe webhook against the purchase email.
 *   - The buyer is auto-logged in after payment.
 *
 * So the account is created *from the purchase*, which is the correct order.
 *
 * Covered by `playwright/tests/billing/guest-checkout-no-login-required.spec.ts`,
 * which is a guard test: if it fails, someone has reintroduced a login wall in
 * front of payment. Fix the wall, not the test.
 */

/** Products purchasable without an account on the unified `/checkout` cart. */
export const GUEST_CHECKOUT_PRODUCT_SLUGS = new Set([
  'ai-bootcamp',
  'ai-bootcamp-self-paced',
  'starter-plan',
  'light-plan',
  'pro-plan',
  'pro-plan-upfront',
]);

/**
 * Dedicated plan checkout paths. These name their product in the URL, so they
 * are always guest-eligible regardless of query string.
 */
export const GUEST_CHECKOUT_PATHS = new Set([
  '/checkout/starter-plan',
  '/checkout/light-plan',
]);

/** True when a dedicated plan checkout path is being visited. */
export const isGuestCheckoutPath = (pathname?: string | null): boolean =>
  Boolean(pathname && GUEST_CHECKOUT_PATHS.has(pathname));

/**
 * True when a `/checkout` URL is buying only guest-eligible products.
 *
 * Conservative on purpose: an unknown slug or a mixed cart falls back to the
 * authenticated flow rather than risking a broken purchase.
 */
export const isGuestCheckoutEligibleSearch = (search?: string | null): boolean => {
  if (!search) {
    return false;
  }

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return false;
  }

  const products = params.getAll('product').filter(Boolean);
  if (products.length !== 1) {
    return false;
  }

  return GUEST_CHECKOUT_PRODUCT_SLUGS.has(products[0]);
};

/** Whether this checkout location may be completed without logging in. */
export const allowsGuestCheckout = (
  pathname?: string | null,
  search?: string | null,
): boolean => isGuestCheckoutPath(pathname) || isGuestCheckoutEligibleSearch(search);

export default allowsGuestCheckout;
